import { useEffect, useMemo, useState } from 'react';
import { Link, useHref, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorState, LoadingState } from '../components/ui/AsyncState';
import { Button } from '../components/ui/Button';
import { useOpsUser } from '../features/ownership/opsUser';
import type { HelpRole, HelpSection } from './content/types';
import { HelpArticle, type ZoomTarget } from './HelpArticle';
import { ImageZoom } from './ImageZoom';
import { orderForRole } from './order';
import { highlightPattern, searchHelp } from './search';
import { useHelpContent } from './useHelpContent';
import styles from './Help.module.css';

const anchor = (id: string) => `help-${id}`;

/** `/help` and `/help/:sectionId`: the whole guide, searchable, in the reader's role order. */
export function HelpPage() {
  const { t } = useTranslation();
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const { role } = useOpsUser();
  const state = useHelpContent();
  const [zoom, setZoom] = useState<ZoomTarget | null>(null);
  const [tocOpen, setTocOpen] = useState(false);

  const groups = useMemo(
    () => (state.status === 'ready' ? orderForRole(state.content, (role as HelpRole | null) ?? null) : []),
    [state, role],
  );
  const hits = useMemo(() => (state.status === 'ready' ? searchHelp(state.content, query) : []), [state, query]);
  const known = state.status === 'ready' && (!sectionId || state.content.sections.some((s) => s.id === sectionId));

  // Deep link: bring the section into view once the content is there.
  useEffect(() => {
    if (state.status !== 'ready' || !sectionId || query) return;
    const el = document.getElementById(anchor(sectionId));
    if (el) {
      el.scrollIntoView?.({ block: 'start' });
      el.focus({ preventScroll: true });
    }
  }, [state.status, sectionId, query]);

  const setQuery = (q: string) => {
    const next = new URLSearchParams(params);
    if (q) next.set('q', q);
    else next.delete('q');
    setParams(next, { replace: true });
  };
  const go = (to: string) => navigate(to);

  if (state.status === 'loading') return <LoadingState label={t('help.loading')} />;
  if (state.status === 'error') return <ErrorState title={t('help.loadError')} onRetry={state.retry} />;

  return (
    <div className={styles.page}>
      <div className={styles.searchRow}>
        <label className={styles.searchLabel} htmlFor="help-search">
          {t('help.page.searchLabel')}
        </label>
        <input
          id="help-search"
          type="search"
          className={styles.search}
          placeholder={t('help.page.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        <p className={styles.hint}>{t('help.page.keyHint')}</p>
      </div>

      {!known && sectionId && <p className={styles.notice}>{t('help.page.sectionNotFound')}</p>}

      <div className={styles.layout}>
        <nav className={styles.toc} aria-label={t('help.page.contents')}>
          <button
            type="button"
            className={styles.tocToggle}
            aria-expanded={tocOpen}
            aria-controls="help-toc-list"
            onClick={() => setTocOpen((o) => !o)}
          >
            {t('help.page.contents')}
          </button>
          <div id="help-toc-list" className={`${styles.tocList} ${tocOpen ? styles.tocListOpen : ''}`}>
            {groups.map((g) => (
              <div key={g.id} className={styles.tocGroup}>
                <p className={styles.tocGroupTitle}>{g.title}</p>
                <ul>
                  {g.sections.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/help/${s.id}`}
                        className={styles.tocLink}
                        aria-current={s.id === sectionId ? 'location' : undefined}
                        onClick={() => setTocOpen(false)}
                      >
                        {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <div className={styles.main}>
          {query ? (
            <SearchResults query={query} hits={hits} onClear={() => setQuery('')} />
          ) : (
            groups.map((g) => (
              <section key={g.id} className={styles.group} aria-label={g.title}>
                <p className={styles.groupTitle}>{g.title}</p>
                {g.sections.map((s) => (
                  <HelpArticle
                    key={s.id}
                    id={anchor(s.id)}
                    section={s}
                    onNavigate={go}
                    onZoom={setZoom}
                    actions={<CopyLink section={s} />}
                  />
                ))}
              </section>
            ))
          )}
        </div>
      </div>
      <ImageZoom target={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}

function CopyLink({ section }: { section: HelpSection }) {
  const { t } = useTranslation();
  const href = useHref(`/help/${section.id}`);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={t('help.page.copyLinkFor', { title: section.title })}
      onClick={() => {
        const url = new URL(href, window.location.href).toString();
        void navigator.clipboard?.writeText(url).then(() => setCopied(true), () => undefined);
      }}
    >
      {copied ? t('help.page.linkCopied') : t('help.page.copyLink')}
    </Button>
  );
}

function Marked({ text, query }: { text: string; query: string }) {
  const re = highlightPattern(query);
  if (!re) return <>{text}</>;
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1 ? <mark key={i}>{p}</mark> : p))}
    </>
  );
}

function SearchResults({ query, hits, onClear }: { query: string; hits: ReturnType<typeof searchHelp>; onClear: () => void }) {
  const { t } = useTranslation();
  if (!hits.length) {
    return (
      <div className={styles.empty} role="status">
        <p className={styles.emptyTitle}>{t('help.page.noResultsTitle', { query })}</p>
        <p className={styles.emptyBody}>{t('help.page.noResultsHint')}</p>
        <Button variant="secondary" size="sm" onClick={onClear}>
          {t('help.page.clearSearch')}
        </Button>
      </div>
    );
  }
  return (
    <div>
      <p className={styles.resultCount} role="status">
        {t('help.page.resultCount', { count: hits.length })}
      </p>
      <ul className={styles.results}>
        {hits.map((h) => (
          <li key={h.section.id}>
            <Link to={`/help/${h.section.id}`} className={styles.result}>
              <span className={styles.resultTitle}>
                <Marked text={h.section.title} query={query} />
              </span>
              <span className={styles.resultSnippet}>
                <Marked text={h.snippet} query={query} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
